/**
 * Framework finder — answer trail, a settled footer, and panel sizing.
 *
 * Ninja Forms renders the multi-part form client-side and only ever keeps the
 * current step in the DOM, rebuilding the header, body and footer regions on
 * every move. Left alone it renders only the chrome each step happens to need,
 * so the panel gained a progress bar, then a Previous button, then a trail as
 * the reader worked through it — and the row reflowed under them each time.
 * Five jobs here, most of them about holding that still:
 *
 *  1. The answer trail, on its own row below the buttons. Ninja Forms has a
 *     breadcrumb of its own, but it lists part titles rather than answers, so
 *     it's hidden in tailwind.css and this builds the real thing.
 *  2. Previous and Next, always both on the row. Multi-part drops the one that
 *     doesn't apply to the step (footer.js), which moved the other button
 *     sideways mid-form; the missing one is replaced with a disabled twin that
 *     holds its place, styled pale in tailwind.css.
 *  3. The progress bar, which the design sits between Previous and Next.
 *     Multi-part renders it into the header region instead, and only once a
 *     second part is visible (header.js) — this form's parts are revealed by
 *     conditional logic, so question one gets none at all. The track on the
 *     button row is this file's own, and its width is mirrored from the
 *     plugin's bar, which stays where it is (hidden in tailwind.css). That
 *     leaves the percentage the plugin's business and gives question one an
 *     empty track rather than nothing.
 *  4. The sentence on the submit step, which reads the answers back before the
 *     button. Its copy lives in the form so it stays editable in Ninja Forms,
 *     with [answer-1], [answer-2] … standing in for the answers; this fills
 *     them from the same trail as job 1.
 *  5. --ff-label-h, the height of the question above the white panel. The panel
 *     is held to 520px, and the CSS min-height has to go on the column that
 *     holds the label as well, so the label's height is added back. It's
 *     measured rather than assumed because a longer question wraps onto a
 *     second line.
 *
 * Everything keys off the DOM rather than Ninja Forms' internals: the plugin's
 * Backbone/Marionette layer is undocumented and changes between releases, but
 * the rendered markup is what the stylesheet already depends on.
 *
 * The pieces this adds are all replaced wholesale whenever Ninja Forms
 * re-renders a region, so each one is (re)placed from a MutationObserver on the
 * region that owns it. Those callbacks are no-ops once everything is where it
 * belongs, which is what stops the observers from feeding themselves.
 */

const SEPARATOR = ' - ';

/** Reads as a sentence with the answers after it: "Your selection: Works - …". */
const TRAIL_PREFIX = 'Your selection:';

/** Placeholders in the submit step's sentence, numbered by question order. */
const SUMMARY_TOKEN = /\[answer-(\d+)\]/g;

/** Multi-part's own fallbacks, for the first render before a button is seen. */
const NAV_LABELS = {
    previous: 'Previous',
    next: 'Next',
};

/**
 * The trail is a row of the panel in its own right, the last one, under the
 * buttons the plugin's footer region holds. It's a child of the column rather than of
 * either region — Ninja Forms replaces everything inside those on every step,
 * and this way there's nothing to put back. tailwind.css orders it into place.
 */
function createBreadcrumb(column) {
    const breadcrumb = document.createElement('div');
    // Empty to begin with, and marked as such rather than hidden: the row holds
    // its place from the first question so the buttons above it don't move down
    // the panel when the first answer arrives. tailwind.css takes it from here.
    breadcrumb.className = 'ff-breadcrumb ff-breadcrumb--empty';

    const trail = document.createElement('div');
    trail.className = 'ff-breadcrumb__trail';
    breadcrumb.appendChild(trail);
    column.appendChild(breadcrumb);

    return { breadcrumb, trail };
}

/**
 * A stand-in for the Previous or Next button Ninja Forms hasn't rendered on
 * this step. Same markup as the plugin's own (tmpl-nf-mp-next-previous), so it
 * picks up the same styling, plus a class the stylesheet fades it with.
 * `disabled` is what makes it inert: a disabled control fires no click, so the
 * plugin's delegated handlers can't be reached through it either.
 */
function createNavPlaceholder(kind) {
    const item = document.createElement('li');
    item.className = `nf-${kind}-item ff-nav-placeholder`;

    const button = document.createElement('input');
    button.type = 'button';
    button.className = `nf-${kind}`;
    button.value = NAV_LABELS[kind];
    button.disabled = true;
    // Nothing here for a keyboard or a screen reader: it's spacing, and the
    // real button appears in its place the moment the step allows the move.
    button.tabIndex = -1;
    button.setAttribute('aria-hidden', 'true');

    item.appendChild(button);
    return item;
}

/**
 * The progress track that sits between Previous and Next. Its own element
 * rather than the plugin's, which is rendered into the header region and only
 * on the steps that have a bar to show; syncProgress() copies the width across.
 */
function createProgressSlot() {
    const item = document.createElement('li');
    item.className = 'ff-progress-item';

    const track = document.createElement('div');
    track.className = 'ff-progress';

    const bar = document.createElement('div');
    bar.className = 'ff-progress__bar';
    bar.style.width = '0%';

    track.appendChild(bar);
    item.appendChild(track);
    return item;
}

/** The field id Ninja Forms puts in the container id: nf-field-84-container. */
function fieldIdOf(container) {
    const match = /nf-field-(\d+)-container/.exec(container.id || '');
    return match ? match[1] : null;
}

/**
 * The chosen answer as the reader saw it — the option's label, not its stored
 * value. Returns null for an unanswered field, including a select still on its
 * "Please select" placeholder (which carries an empty value).
 */
function answerOf(container) {
    const checked = container.querySelector('input[type="radio"]:checked');
    if (checked) {
        const label = container.querySelector(`label[for="${checked.id}"]`) || checked.nextElementSibling;
        return label ? label.textContent.trim() : null;
    }

    const select = container.querySelector('select');
    if (select && select.value !== '') {
        const option = select.selectedOptions[0];
        return option ? option.textContent.trim() : null;
    }

    return null;
}

/**
 * The submit step's sentence with the reader's own answers in it.
 *
 * The copy is authored in Ninja Forms (the HTML field on the last part) so it
 * can be edited there, and carries [answer-1], [answer-2] … in place of the
 * answers, numbered in the order the questions were asked. A token with no
 * answer behind it takes its whole clause with it rather than leaving a hole in
 * the sentence: the works branch asks three questions and the services branch
 * two, so the closing clause isn't always earned. Clauses are the
 * comma-separated parts of the copy — a comma is what marks one — and the full
 * stop is put back on whichever clause ends up last.
 */
function composeSummary(template, given) {
    const clauses = template
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\.\s*$/, '')
        .split(',')
        // A clause with no token in it is fixed copy and always stands.
        .filter((clause) => [...clause.matchAll(SUMMARY_TOKEN)].every(([, n]) => given[n - 1]))
        // Sentence fragments use lower case; the option labels and trail keep
        // their original presentation.
        .map((clause) => clause.replace(SUMMARY_TOKEN, (token, n) => given[n - 1].toLowerCase()));

    return `${clauses.join(',')}.`;
}

function initFrameworkFinder() {
    const column = document.querySelector('.framework-finder .nf-form-content > div');
    const body = column && column.querySelector('.nf-mp-body');
    const header = column && column.querySelector('.nf-mp-header');
    const footer = column && column.querySelector('.nf-mp-footer');
    if (!body || !header || !footer) {
        return;
    }

    const { breadcrumb, trail } = createBreadcrumb(column);

    // The multi-part settings object carries the labels set in the form
    // builder; without it the plugin's own defaults above stand in.
    const settings = window.nfMPSettings || {};
    NAV_LABELS.previous = settings.prevLabel || NAV_LABELS.previous;
    NAV_LABELS.next = settings.nextLabel || NAV_LABELS.next;

    /**
     * The plugin's bar, from the header region it renders into. Absent on the
     * first question, where it has only one visible part and draws nothing.
     */
    const syncProgress = () => {
        const source = header.querySelector('.nf-progress');
        const bar = footer.querySelector('.ff-progress__bar');
        if (bar) {
            bar.style.width = (source && source.style.width) || '0%';
        }
    };

    // The footer's contents are replaced wholesale on every step, taking the
    // track and the placeholders with them. Rebuild the row left to right:
    // Previous, the progress track, Next — filling in whichever of the two
    // buttons the plugin left out.
    const normaliseFooter = () => {
        const row = footer.firstElementChild;
        if (!row) {
            return;
        }

        // Absent entirely on the first question, where the plugin has neither
        // button to show and renders nothing at all.
        let list = row.querySelector('.nf-next-previous');
        if (!list) {
            list = document.createElement('ul');
            list.className = 'nf-next-previous';
            row.appendChild(list);
        }

        // A real button is the authority on its own label, so take it while
        // it's on screen and the placeholder that replaces it later matches.
        const previous = list.querySelector('.nf-previous');
        const next = list.querySelector('.nf-next');
        if (previous) {
            NAV_LABELS.previous = previous.value;
        } else {
            list.insertBefore(createNavPlaceholder('previous'), list.firstChild);
        }
        if (next) {
            NAV_LABELS.next = next.value;
        } else {
            list.appendChild(createNavPlaceholder('next'));
        }

        // Between the two, so the track is centred on the row whatever the
        // buttons either side of it are doing. Guarded rather than simply
        // inserted: moving a node already in position still reports a mutation,
        // which would send this observer round again for good.
        const nextItem = list.querySelector('.nf-next-item');
        const slot = list.querySelector('.ff-progress-item') || createProgressSlot();
        if (slot.parentElement !== list || slot.nextElementSibling !== nextItem) {
            list.insertBefore(slot, nextItem);
        }

        syncProgress();
    };

    // Answers in the order they were given, which is also the order of the
    // steps: [{ fieldId, answer }]. Truncating at a field drops that answer and
    // everything downstream of it in one go.
    let answers = [];

    const currentFields = () => [...body.querySelectorAll('.nf-field-container')];

    const truncateAt = (fieldId) => {
        const index = answers.findIndex((entry) => entry.fieldId === fieldId);
        if (index > -1) {
            answers = answers.slice(0, index);
        }
    };

    const record = (container) => {
        const fieldId = fieldIdOf(container);
        const answer = answerOf(container);
        if (!fieldId || !answer) {
            return;
        }
        truncateAt(fieldId);
        answers.push({ fieldId, answer });
    };

    /**
     * Ninja Forms renders the submit step afresh every time the reader arrives
     * at it, so the sentence's template is read back off the paragraph the
     * first time it's seen and kept on the element for the rewrites after it.
     * Written only when it actually changes: this paragraph is inside the body
     * region, and setting textContent replaces the node underneath it whether
     * the text differs or not — which the observer watching that region would
     * report, and answer by calling this again.
     */
    const fillSummary = () => {
        const summary = body.querySelector('.ff-summary');
        if (!summary) {
            return;
        }

        if (!summary.dataset.ffTemplate) {
            summary.dataset.ffTemplate = summary.textContent;
        }

        const sentence = composeSummary(
            summary.dataset.ffTemplate,
            answers.map((entry) => entry.answer),
        );
        if (summary.textContent !== sentence) {
            summary.textContent = sentence;
        }
    };

    const render = () => {
        const empty = answers.length === 0;
        trail.textContent = empty
            ? ''
            : `${TRAIL_PREFIX} ${answers.map((entry) => entry.answer).join(SEPARATOR)}`;
        breadcrumb.classList.toggle('ff-breadcrumb--empty', empty);
        fillSummary();
    };

    /**
     * The label box standing above the panel on this step. Not simply the first
     * .nf-field-label in the body: every field carries one, and the submit
     * step's HTML field has its own hidden by Ninja Forms — measuring that
     * published a height of nothing and left the panel short. The first
     * *rendered* label is the one holding the space, whether it has a question
     * in it (every question step) or is the empty box the submit field
     * contributes, which the stylesheet keeps at the height of a question so
     * the panel doesn't move on the last step either.
     */
    const stepLabel = () =>
        [...body.querySelectorAll('.nf-field-label')].find((label) => label.offsetParent !== null) || null;

    const publishLabelHeight = (label) => {
        if (!label) {
            column.style.setProperty('--ff-label-h', '0px');
            return;
        }

        const style = getComputedStyle(label);
        const height = label.getBoundingClientRect().height + (parseFloat(style.marginBottom) || 0);
        column.style.setProperty('--ff-label-h', `${Math.round(height)}px`);
    };

    // One observer, re-pointed at each step's question, so a label that rewraps
    // on resize (or once the webfont lands) republishes its height.
    const labelObserver = new ResizeObserver(([entry]) => publishLabelHeight(entry.target));

    // Published from here as well as from the observer above: the observer's
    // first delivery is a frame away, and a question that arrives already at
    // its final size never produces a second one.
    const measureLabel = () => {
        labelObserver.disconnect();
        const label = stepLabel();
        publishLabelHeight(label);
        if (label) {
            labelObserver.observe(label);
        }
    };

    // Answering the current question shows it in the trail straight away.
    column.addEventListener('change', (event) => {
        const container = event.target.closest('.nf-field-container');
        if (container) {
            record(container);
            render();
        }
    });

    // Ninja Forms replaces the step's markup as soon as either button is
    // pressed, so the answer on screen is banked first. This is what keeps a
    // re-visited question in the trail when the reader goes back and then
    // moves forward again on the same answer, without touching it.
    column.addEventListener(
        'click',
        (event) => {
            if (event.target.closest('.nf-next, .nf-previous')) {
                currentFields().forEach(record);
            }
        },
        true,
    );

    // Landing on a question — going back, or a route change putting a
    // previously answered question in front of the reader again — clears that
    // answer and everything after it. Re-answering puts it back.
    // Run straight from the observer callback rather than off a rendering
    // frame: Ninja Forms rebuilds the step synchronously, so the DOM is already
    // final by the time this is delivered, and it still runs when the tab is in
    // the background and frames aren't being produced.
    const onStepRender = () => {
        currentFields().forEach((container) => {
            const fieldId = fieldIdOf(container);
            if (fieldId) {
                truncateAt(fieldId);
            }
        });
        measureLabel();
        render();
    };

    new MutationObserver(onStepRender).observe(body, { childList: true, subtree: true });

    // subtree, because Ninja Forms empties these rows in place as often as it
    // replaces them, and only the deeper mutation is reported in that case. The
    // callbacks are no-ops once the row is in order, so the elements they add —
    // and the trail's own text updates — can't send them round again.
    new MutationObserver(normaliseFooter).observe(footer, { childList: true, subtree: true });
    new MutationObserver(syncProgress).observe(header, { childList: true, subtree: true });

    onStepRender();
    normaliseFooter();

    // The question is set in a webfont; re-measure once it's swapped in, since
    // the fallback can wrap the same text onto a different number of lines.
    if (document.fonts) {
        document.fonts.ready.then(measureLabel);
    }
}

/**
 * Ninja Forms boots after this route fires and builds the form from its own
 * templates, so the markup is usually not there yet on DOM ready. Wait for the
 * form's own root to appear rather than guessing at a delay.
 */
function whenFormRendered(callback) {
    const container = document.querySelector('.framework-finder');
    if (!container) {
        return;
    }

    if (container.querySelector('.nf-form-content > div .nf-mp-body')) {
        callback();
        return;
    }

    const observer = new MutationObserver(() => {
        if (container.querySelector('.nf-form-content > div .nf-mp-body')) {
            observer.disconnect();
            callback();
        }
    });
    observer.observe(container, { childList: true, subtree: true });
}

export default {
    init() {
        whenFormRendered(initFrameworkFinder);
    },
};
